import axios from 'axios';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { api } from '../../Api';
import { Store } from '../../Store';
import { getError } from '../../utils';
import {
  downloadStickerPdf,
  stickerPreviewSvg,
  resolveSticker,
  sheetPlan,
  presetIdFor,
  STICKER_PRESETS,
} from '../../utils/stickerPdf';
import '../../assets/ops.css';

// Stock room: add units to a product, print their 2-inch stickers, and see
// exactly which serials are on the shelf.
//
// A product is created empty; every physical piece enters here, which is what
// mints its serial. That is what makes a delivery traceable to the exact unit
// that left the counter.

export default function StockScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [addFor, setAddFor] = useState(null);
  const [printFor, setPrintFor] = useState(null);
  const [unitsFor, setUnitsFor] = useState(null);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${userInfo?.token}` }),
    [userInfo]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: payload } = await axios.get(`${api}/api/stock/overview`, {
        headers,
        params: { page, search },
      });
      setData(payload);
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setLoading(false);
    }
  }, [headers, page, search]);

  useEffect(() => {
    load();
  }, [load]);

  // Debounce the search so typing doesn't fire a request per keystroke.
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const summary = data?.summary || {};
  const products = data?.products || [];

  return (
    <div className="ops">
      <Helmet><title>Stock & Stickers</title></Helmet>

      <div className="ops-head">
        <div>
          <h2>Stock &amp; Stickers</h2>
          <p>
            Add units to a product to mint their serials, then print a 2-inch sticker
            for each one. Every serial is tracked from the shelf to the customer.
          </p>
        </div>
        <button className="ops-btn" onClick={load} disabled={loading}>
          <i className="fa fa-refresh" /> Refresh
        </button>
      </div>

      <div className="ops-stats">
        <div className="ops-stat">
          <div className="label">On the shelf</div>
          <div className="value">{Number(summary.stock || 0).toLocaleString()}</div>
          <div className="hint">across {summary.products || 0} products</div>
        </div>
        <div className="ops-stat">
          <div className="label">Reserved</div>
          <div className="value">{Number(summary.reserved || 0).toLocaleString()}</div>
          <div className="hint">claimed by open orders</div>
        </div>
        <div className="ops-stat">
          <div className="label">Available</div>
          <div className="value">
            {Math.max(Number(summary.stock || 0) - Number(summary.reserved || 0), 0).toLocaleString()}
          </div>
          <div className="hint">free to sell</div>
        </div>
        <div className="ops-stat">
          <div className="label">Out of stock</div>
          <div className="value" style={{ color: summary.outOfStock ? 'var(--ops-red)' : undefined }}>
            {summary.outOfStock || 0}
          </div>
          <div className="hint">products with zero units</div>
        </div>
      </div>

      <div className="ops-toolbar">
        <div className="grow">
          <input
            className="ops-input"
            placeholder="Search products by name…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
      </div>

      {loading && !data ? (
        <div className="ops-empty"><i className="fa fa-spinner fa-spin" />Loading stock…</div>
      ) : !products.length ? (
        <div className="ops-empty">
          <i className="fa fa-cube" />
          No products found{search ? ` for “${search}”` : ''}.
        </div>
      ) : (
        <div className="ops-table-wrap">
          <table className="ops-table">
            <thead>
              <tr>
                <th>Product</th>
                <th className="num">Shelf</th>
                <th className="num">Reserved</th>
                <th className="num">Available</th>
                <th className="ops-hide-sm">Status</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr key={product._id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <img className="ops-thumb" src={product.image} alt="" />
                      <div style={{ minWidth: 0 }}>
                        <strong style={{ display: 'block', fontSize: 13.5 }}>{product.name}</strong>
                        <span style={{ fontSize: 11.5, color: 'var(--ops-muted)' }}>
                          ৳{Number(product.price || 0).toLocaleString()} · {product.point || 0} PV
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="num"><strong>{product.stock}</strong></td>
                  <td className="num" style={{ color: 'var(--ops-muted)' }}>{product.reserved}</td>
                  <td className="num">
                    <strong style={{ color: product.available > 0 ? 'var(--ops-green)' : 'var(--ops-red)' }}>
                      {product.available}
                    </strong>
                  </td>
                  <td className="ops-hide-sm">
                    {product.stock <= 0 ? (
                      <span className="ops-badge red">Out of stock</span>
                    ) : product.low ? (
                      <span className="ops-badge amber">Low</span>
                    ) : (
                      <span className="ops-badge green">In stock</span>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      <button className="ops-btn sm primary" onClick={() => setAddFor(product)}>
                        <i className="fa fa-plus" /> Stock
                      </button>
                      <button
                        className="ops-btn sm"
                        onClick={() => setPrintFor({ product })}
                        disabled={product.stock <= 0}
                        title={product.stock <= 0 ? 'Nothing to print yet' : 'Print stickers'}
                      >
                        <i className="fa fa-print" />
                      </button>
                      <button className="ops-btn sm" onClick={() => setUnitsFor(product)} title="View serials">
                        <i className="fa fa-list-ul" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data?.pages > 1 && (
        <div className="ops-pager">
          <button className="ops-btn sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </button>
          <span>Page {data.page} of {data.pages}</span>
          <button className="ops-btn sm" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}>
            Next
          </button>
        </div>
      )}

      <BatchHistory headers={headers} onPrint={(batch) => setPrintFor({ batch })} />

      {addFor && (
        <AddStockModal
          product={addFor}
          headers={headers}
          onClose={() => setAddFor(null)}
          onDone={(result) => {
            setAddFor(null);
            load();
            // Straight from adding stock into printing its stickers — that is
            // always the next thing the stock room does.
            setPrintFor({ batch: result.batch, justAdded: true });
          }}
        />
      )}

      {printFor && (
        <PrintStickersModal
          target={printFor}
          headers={headers}
          settings={data?.settings}
          onClose={() => setPrintFor(null)}
        />
      )}

      {unitsFor && (
        <UnitsModal product={unitsFor} headers={headers} onClose={() => setUnitsFor(null)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add stock
// ---------------------------------------------------------------------------
function AddStockModal({ product, headers, onClose, onDone }) {
  const [quantity, setQuantity] = useState('');
  const [note, setNote] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [saving, setSaving] = useState(false);

  const qty = Number(quantity);
  const valid = Number.isInteger(qty) && qty > 0 && qty <= 5000;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${api}/api/stock/add`,
        { productId: product._id, quantity: qty, note, unitCost: Number(unitCost) || 0 },
        { headers }
      );
      toast.success(data.message);
      onDone(data);
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ops-overlay" onClick={onClose}>
      <form className="ops-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="ops-modal-head">
          <div>
            <h4>Add stock</h4>
            <p className="sub">{product.name} · currently {product.stock} on the shelf</p>
          </div>
          <button type="button" className="ops-x" onClick={onClose}><i className="fa fa-times" /></button>
        </div>

        <div className="ops-modal-body">
          <div className="ops-field">
            <label className="ops-label">Quantity to add</label>
            <input
              className="ops-input"
              type="number"
              min="1"
              max="5000"
              autoFocus
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="e.g. 50"
            />
            {quantity && !valid && (
              <p style={{ color: 'var(--ops-red)', fontSize: 12, margin: '6px 0 0' }}>
                Enter a whole number between 1 and 5000.
              </p>
            )}
          </div>

          <div className="ops-grid">
            <div className="ops-field">
              <label className="ops-label">Unit cost (optional)</label>
              <input
                className="ops-input"
                type="number"
                min="0"
                step="0.01"
                value={unitCost}
                onChange={(e) => setUnitCost(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="ops-field">
              <label className="ops-label">Note (optional)</label>
              <input
                className="ops-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Supplier, invoice no…"
              />
            </div>
          </div>

          {valid && (
            <div className="ops-note info">
              <i className="fa fa-info-circle" />
              <span>
                This mints <strong>{qty}</strong> serial{qty > 1 ? 's' : ''} and takes the shelf from{' '}
                <strong>{product.stock}</strong> to <strong>{product.stock + qty}</strong>. You can print
                the stickers right after.
              </span>
            </div>
          )}
        </div>

        <div className="ops-modal-foot">
          <button type="button" className="ops-btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="ops-btn primary" disabled={!valid || saving}>
            {saving ? <><i className="fa fa-spinner fa-spin" /> Adding…</> : <><i className="fa fa-plus" /> Add {valid ? qty : ''} unit{qty === 1 ? '' : 's'}</>}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Print stickers
// ---------------------------------------------------------------------------
function PrintStickersModal({ target, headers, onClose }) {
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);
  const [scope, setScope] = useState(target.batch ? 'batch' : 'in_stock');
  // Starts from the saved design and can be overridden for this print only —
  // handy when a different roll is loaded than the usual one.
  const [design, setDesign] = useState(null);

  const title = target.batch
    ? `Batch ${target.batch.batchCode}`
    : target.product?.name;

  const fetchStickers = async () => {
    setLoading(true);
    try {
      const body = target.batch && scope === 'batch'
        ? { batchId: target.batch._id }
        : { productId: target.product?._id || target.batch?.product, status: scope === 'all' ? '' : 'in_stock' };
      const { data } = await axios.post(`${api}/api/stock/stickers`, body, { headers });
      setPayload(data);
      // Only adopt the saved design the first time; keep any override the
      // operator has already made while changing scope.
      setDesign((current) => current || resolveSticker(data.sticker));
    } catch (err) {
      toast.error(getError(err));
      setPayload(null);
    } finally {
      setLoading(false);
    }
  };

  // Load once when the dialog opens, and again if the scope changes.
  useEffect(() => {
    fetchStickers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  const download = () => {
    if (!payload?.units?.length || !design) return;
    downloadStickerPdf({ units: payload.units, product: payload.product, sticker: design });
    toast.success(`${payload.units.length} sticker(s) downloaded.`);
  };

  const set = (key, value) => setDesign((current) => ({ ...current, [key]: value }));

  const plan = design ? sheetPlan(design) : null;
  const preview = payload?.units?.[0] && design
    ? stickerPreviewSvg({
        unit: payload.units[0],
        product: payload.product,
        sticker: design,
        maxPx: 200,
      })
    : null;

  const sheets = payload && plan ? Math.ceil(payload.units.length / Math.max(plan.perPage, 1)) : 0;
  const presetId = design ? presetIdFor(design.widthMm, design.heightMm) : 'custom';

  return (
    <div className="ops-overlay" onClick={onClose}>
      <div className="ops-modal wide" onClick={(e) => e.stopPropagation()}>
        <div className="ops-modal-head">
          <div>
            <h4>Print stickers</h4>
            <p className="sub">{title}</p>
          </div>
          <button className="ops-x" onClick={onClose}><i className="fa fa-times" /></button>
        </div>

        <div className="ops-modal-body">
          {target.justAdded && (
            <div className="ops-note ok" style={{ marginBottom: 14 }}>
              <i className="fa fa-check-circle" />
              <span>Stock added. Print the stickers for these units now so every piece is labelled.</span>
            </div>
          )}

          <div className="ops-grid two" style={{ alignItems: 'start' }}>
            <div>
              <div className="ops-field">
                <label className="ops-label">Which units</label>
                <select className="ops-select" value={scope} onChange={(e) => setScope(e.target.value)}>
                  {target.batch && <option value="batch">Just this batch</option>}
                  <option value="in_stock">All units currently on the shelf</option>
                  <option value="all">Every unit ever added</option>
                </select>
              </div>

              {design && (
                <>
                  <div className="ops-field">
                    <label className="ops-label">Output</label>
                    <select
                      className="ops-select"
                      value={design.format}
                      onChange={(e) => set('format', e.target.value)}
                    >
                      <option value="a4">A4 sheet — tile many per page</option>
                      <option value="label">Label roll — one per page (thermal printer)</option>
                    </select>
                  </div>

                  <div className="ops-field">
                    <label className="ops-label">Label size</label>
                    <select
                      className="ops-select"
                      value={presetId}
                      onChange={(e) => {
                        const preset = STICKER_PRESETS.find((p) => p.id === e.target.value);
                        if (!preset) return;
                        setDesign((c) => ({ ...c, widthMm: preset.widthMm, heightMm: preset.heightMm }));
                      }}
                    >
                      {STICKER_PRESETS.map((p) => (
                        <option key={p.id} value={p.id}>{p.label}</option>
                      ))}
                      <option value="custom">Custom…</option>
                    </select>
                  </div>

                  <div style={{ display: 'flex', gap: 10 }}>
                    <div className="ops-field" style={{ flex: 1 }}>
                      <label className="ops-label">Width (mm)</label>
                      <input
                        className="ops-input"
                        type="number"
                        value={design.widthMm}
                        onChange={(e) => set('widthMm', Number(e.target.value))}
                      />
                    </div>
                    <div className="ops-field" style={{ flex: 1 }}>
                      <label className="ops-label">Height (mm)</label>
                      <input
                        className="ops-input"
                        type="number"
                        value={design.heightMm}
                        onChange={(e) => set('heightMm', Number(e.target.value))}
                      />
                    </div>
                  </div>
                  <p style={{ fontSize: 11.5, color: 'var(--ops-muted)', margin: '-4px 0 12px' }}>
                    Changes here apply to this print only. Set the default in Developer → Sticker Design.
                  </p>
                </>
              )}

              {loading ? (
                <div className="ops-empty" style={{ padding: 24 }}>
                  <i className="fa fa-spinner fa-spin" />Preparing stickers…
                </div>
              ) : payload && plan ? (
                <>
                  <div className="ops-note info">
                    <i className="fa fa-file-pdf-o" />
                    <span>
                      <strong>{payload.units.length}</strong> sticker{payload.units.length === 1 ? '' : 's'} ·{' '}
                      {sheets} {design.format === 'a4'
                        ? `A4 page${sheets === 1 ? '' : 's'} (${plan.columns} × ${plan.rows} per page)`
                        : `label${sheets === 1 ? '' : 's'}`}
                      {payload.isReprint && ' · includes previously printed units'}
                    </span>
                  </div>
                  {design.format === 'a4' && !plan.fitsOnA4 && (
                    <div className="ops-note error" style={{ marginTop: 10 }}>
                      <i className="fa fa-exclamation-triangle" />
                      <span>This label is too large for an A4 sheet. Reduce the size or switch to label-roll output.</span>
                    </div>
                  )}
                  {design.showBarcode && !plan.barcodeOk && (
                    <div className={`ops-note ${plan.barcodeTight ? 'warn' : 'error'}`} style={{ marginTop: 10 }}>
                      <i className="fa fa-barcode" />
                      <span>
                        Bars are {plan.moduleMm.toFixed(2)} mm wide.{' '}
                        {plan.barcodeTight
                          ? 'Most scanners cope, but a wider label reads more reliably.'
                          : 'That is too narrow to scan reliably — use a wider label.'}
                      </span>
                    </div>
                  )}
                  {payload.isReprint && (
                    <div className="ops-note warn" style={{ marginTop: 10 }}>
                      <i className="fa fa-exclamation-triangle" />
                      <span>Some of these stickers were printed before. The reprint is recorded in the log.</span>
                    </div>
                  )}
                </>
              ) : (
                <div className="ops-note warn">
                  <i className="fa fa-exclamation-triangle" />
                  <span>No units matched. Add stock first.</span>
                </div>
              )}
            </div>

            <div>
              <label className="ops-label">
                Preview{design ? ` — ${Number(design.widthMm).toFixed(1)} × ${Number(design.heightMm).toFixed(1)} mm` : ''}
              </label>
              <div
                style={{
                  display: 'grid',
                  placeItems: 'center',
                  background: '#f8fafc',
                  border: '1px solid var(--ops-line)',
                  borderRadius: 12,
                  padding: 16,
                  minHeight: 200,
                }}
              >
                {preview ? (
                  <div dangerouslySetInnerHTML={{ __html: preview }} />
                ) : (
                  <span style={{ color: 'var(--ops-muted)', fontSize: 13 }}>Nothing to preview</span>
                )}
              </div>
              {payload?.units?.length > 1 && (
                <p style={{ fontSize: 11.5, color: 'var(--ops-muted)', marginTop: 8, textAlign: 'center' }}>
                  {payload.units[0].serial} … {payload.units[payload.units.length - 1].serial}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="ops-modal-foot">
          <button className="ops-btn" onClick={onClose}>Close</button>
          <button className="ops-btn primary" onClick={download} disabled={!payload?.units?.length}>
            <i className="fa fa-download" /> Download PDF
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Units of a product
// ---------------------------------------------------------------------------
function UnitsModal({ product, headers, onClose }) {
  const [status, setStatus] = useState('in_stock');
  const [units, setUnits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    let live = true;
    setLoading(true);
    axios
      .get(`${api}/api/stock/units`, {
        headers,
        params: { productId: product._id, status, page, pageSize: 50 },
      })
      .then(({ data }) => { if (live) setUnits(data); })
      .catch((err) => toast.error(getError(err)))
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [headers, product._id, status, page]);

  const statusBadge = (s) =>
    s === 'in_stock' ? <span className="ops-badge green">On shelf</span>
      : s === 'delivered' ? <span className="ops-badge blue">Delivered</span>
      : <span className="ops-badge grey">Void</span>;

  return (
    <div className="ops-overlay" onClick={onClose}>
      <div className="ops-modal wide" onClick={(e) => e.stopPropagation()}>
        <div className="ops-modal-head">
          <div>
            <h4>Serials</h4>
            <p className="sub">{product.name}</p>
          </div>
          <button className="ops-x" onClick={onClose}><i className="fa fa-times" /></button>
        </div>

        <div className="ops-modal-body">
          <div className="ops-toolbar">
            <select
              className="ops-select"
              style={{ maxWidth: 220 }}
              value={status}
              onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            >
              <option value="in_stock">On the shelf</option>
              <option value="delivered">Delivered</option>
              <option value="void">Voided</option>
              <option value="">All</option>
            </select>
            {units && <span style={{ color: 'var(--ops-muted)', fontSize: 13 }}>{units.total} unit(s)</span>}
          </div>

          {loading ? (
            <div className="ops-empty"><i className="fa fa-spinner fa-spin" />Loading…</div>
          ) : !units?.units?.length ? (
            <div className="ops-empty"><i className="fa fa-barcode" />No units in this state.</div>
          ) : (
            <div className="ops-table-wrap">
              <table className="ops-table" style={{ minWidth: 560 }}>
                <thead>
                  <tr>
                    <th>Serial</th>
                    <th>Batch</th>
                    <th>Status</th>
                    <th className="ops-hide-sm">Delivered to</th>
                    <th className="ops-hide-sm">When</th>
                  </tr>
                </thead>
                <tbody>
                  {units.units.map((unit) => (
                    <tr key={unit._id}>
                      <td className="ops-mono">{unit.serial}</td>
                      <td className="ops-mono" style={{ color: 'var(--ops-muted)' }}>{unit.batchCode}</td>
                      <td>{statusBadge(unit.status)}</td>
                      <td className="ops-hide-sm">{unit.deliveredToUsername || '—'}</td>
                      <td className="ops-hide-sm" style={{ color: 'var(--ops-muted)', fontSize: 12 }}>
                        {unit.deliveredAt ? new Date(unit.deliveredAt).toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {units?.pages > 1 && (
            <div className="ops-pager">
              <button className="ops-btn sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
              <span>Page {units.page} of {units.pages}</span>
              <button className="ops-btn sm" disabled={page >= units.pages} onClick={() => setPage((p) => p + 1)}>Next</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recent batches
// ---------------------------------------------------------------------------
function BatchHistory({ headers, onPrint }) {
  const [batches, setBatches] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    axios
      .get(`${api}/api/stock/batches`, { headers, params: { page, pageSize: 10 } })
      .then(({ data }) => { if (live) setBatches(data); })
      .catch(() => {})
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [headers, page]);

  return (
    <div className="ops-card" style={{ marginTop: 20 }}>
      <h3>Stock added recently</h3>
      <p className="sub">Every stock-add, with the serial range it created and how many of those units are still on the shelf.</p>

      {loading && !batches ? (
        <div className="ops-empty"><i className="fa fa-spinner fa-spin" />Loading…</div>
      ) : !batches?.batches?.length ? (
        <div className="ops-empty"><i className="fa fa-inbox" />No stock has been added yet.</div>
      ) : (
        <div className="ops-table-wrap">
          <table className="ops-table" style={{ minWidth: 780 }}>
            <thead>
              <tr>
                <th>Batch</th>
                <th>Product</th>
                <th className="num">Qty</th>
                <th className="ops-hide-sm">Serials</th>
                <th className="num ops-hide-sm">On shelf</th>
                <th className="ops-hide-sm">Added by</th>
                <th style={{ textAlign: 'right' }}>Stickers</th>
              </tr>
            </thead>
            <tbody>
              {batches.batches.map((batch) => (
                <tr key={batch._id}>
                  <td className="ops-mono">{batch.batchCode}</td>
                  <td>
                    <strong style={{ fontSize: 13 }}>{batch.productName}</strong>
                    <div style={{ fontSize: 11, color: 'var(--ops-muted)' }}>
                      {new Date(batch.createdAt).toLocaleString()}
                    </div>
                  </td>
                  <td className="num"><strong>{batch.quantity}</strong></td>
                  <td className="ops-mono ops-hide-sm" style={{ fontSize: 11.5, color: 'var(--ops-muted)' }}>
                    {batch.serialFrom}<br />→ {batch.serialTo}
                  </td>
                  <td className="num ops-hide-sm">{batch.units?.in_stock ?? 0}</td>
                  <td className="ops-hide-sm" style={{ fontSize: 12.5 }}>{batch.createdByName || '—'}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="ops-btn sm" onClick={() => onPrint(batch)}>
                      <i className="fa fa-print" />{' '}
                      {batch.printCount > 0 ? `Reprint (${batch.printCount})` : 'Print'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {batches?.pages > 1 && (
        <div className="ops-pager">
          <button className="ops-btn sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
          <span>Page {batches.page} of {batches.pages}</span>
          <button className="ops-btn sm" disabled={page >= batches.pages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}
